import {
  type ConferenceFilter,
  type PlayerItemResponsePagedData,
  rdbV1FiltersYearsList,
  type TeamConferenceFilter,
} from '@on3/api';
import type { ICommitsIndexProps } from '@on3/ui-lib/src/api/schema/custom-contracts';
import { withAuthentication } from '@on3/ui-lib/src/hocs/withAuthentication';
import { withValidUrl } from '@on3/ui-lib/src/hocs/withValidUrl';
import { proxyApi } from '@on3/ui-lib/utils/api';
import { capitalizeFirstLetter } from '@on3/ui-lib/utils/capitalizeFirstLetter';
import { slugify } from '@on3/ui-lib/utils/slugify';
import { TransfersList } from 'components/College/Interests/Transfers/TransfersList';
import { PageWrapper } from 'components/Layouts/PageWrapper';
import type { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import type { FC } from 'react';

const TransfersIndex: FC<ICommitsIndexProps> = (props) => {
  const { currentSite } = props?.siteData || {};
  const router = useRouter();
  const { year, sport } = router.query;
  const capitilzedSport = capitalizeFirstLetter(`${sport}`);
  const orgName =
    currentSite?.organization?.knownAs ?? currentSite?.organization?.name;
  const orgMascot = currentSite?.organization?.mascot;
  const title = `${year} ${orgName} ${orgMascot} ${capitilzedSport} Transfers`;
  const description = `${year} ${orgName} ${orgMascot} ${capitilzedSport} team transfers list.`;
  const noIndex = props?.commitList?.pagination?.count === 0;

  return (
    <PageWrapper
      {...props}
      description={description}
      noIndex={noIndex}
      title={title}
    >
      <TransfersList
        {...props}
        key={`${year}-${sport}-${orgName}`}
        title={title}
        toggled={false}
      />
    </PageWrapper>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const siteData = await withValidUrl.isValid(ctx);
  const userData = await withAuthentication.isAuth(ctx);

  const orgKey = siteData?.currentSite?.organizationKey;
  const { year, sport } = ctx.query;

  const { rankingSports } = siteData || {};
  const sportKey = rankingSports?.find(
    (sp) => sp?.name && slugify(sp?.name) === sport,
  )?.key;

  if (!orgKey || !year || !sport || !sportKey) {
    return {
      notFound: true,
    };
  }

  try {
    const [commitList, teams, years, conferences] = await Promise.allSettled([
      proxyApi
        .get<PlayerItemResponsePagedData>(
          `/public/rdb/v2/organizations/${orgKey}/transfers?sportKey=${sportKey}&year=${year}`,
        )
        .then((r) => r.data),
      proxyApi
        .get<TeamConferenceFilter[]>(
          `/public/rdb/v1/filters/teams?groupBy=true&sportKey=${sportKey}&year=${year}`,
        )
        .then((r) => r.data),
      rdbV1FiltersYearsList(proxyApi),
      proxyApi
        .get<ConferenceFilter[]>(`/public/rdb/v1/filters/conferences`)
        .then((r) => r.data),
    ]);

    if (commitList.status === 'rejected') {
      return {
        notFound: true,
      };
    }

    const commitListData =
      commitList.status === 'fulfilled' ? commitList.value : [];

    const data = {
      pageType: 'database',
      siteData: siteData || [],
      userData: userData || {},
      commitList: commitListData,
      teams: teams.status === 'fulfilled' ? teams.value : [],
      years: years.status === 'fulfilled' ? years.value : [],
      conferences: conferences.status === 'fulfilled' ? conferences.value : [],
      sports: rankingSports,
    };

    return { props: { ...data } };
  } catch (e) {
    return { props: [] };
  }
};

export default withValidUrl(TransfersIndex);
